import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { throwFromResponse } from '../lib/apiErrorMessage';
import type {
  BulkAdjustResult,
  MileageAdjustPayload,
  MileageBalance,
  MileageBulkAdjustPayload,
  MileageEarnLot,
  MileagePagination,
  MileageTransaction,
  MileageTransactionType,
  PagedResult,
} from '../types';

const BASE = '/api/admin/v1/mileage';

interface BalanceEnvelope {
  success: boolean;
  data?: MileageBalance | null;
}

/** data 는 직렬화된 Spring Page (content + page metadata), pagination 은 meta.pagination(0-based). */
interface PageEnvelope<T> {
  success: boolean;
  data?: { content?: T[] } | null;
  meta?: { pagination?: { page: number; size: number; totalItems: number; totalPages: number } } | null;
}

interface BulkResultEnvelope {
  success: boolean;
  data?: BulkAdjustResult | null;
}

export interface MileageTransactionQuery {
  userId?: string | undefined;
  type?: MileageTransactionType | undefined;
  occurredFrom?: string | undefined;
  occurredTo?: string | undefined;
  /** UI 1-based. fetch 시 BE 0-based Pageable 로 변환. */
  page: number;
  size: number;
}

export interface MileageExpiringQuery {
  days: number;
  page: number;
  size: number;
}

export const mileageBalanceQueryKey = (userId: string) =>
  ['admin', 'mileage', 'balance', userId] as const;

export const mileageTransactionsQueryKey = (q: MileageTransactionQuery) =>
  [
    'admin',
    'mileage',
    'transactions',
    q.userId ?? '',
    q.type ?? '',
    q.occurredFrom ?? '',
    q.occurredTo ?? '',
    q.page,
    q.size,
  ] as const;

export const mileageExpiringQueryKey = (q: MileageExpiringQuery) =>
  ['admin', 'mileage', 'expiring', q.days, q.page, q.size] as const;

/** BE meta.pagination.page 는 0-based → Pagination primitive 는 1-based 이므로 +1. */
const toPagination = (
  meta: PageEnvelope<unknown>['meta'],
  fallback: { page: number; size: number; count: number },
): MileagePagination => {
  const p = meta?.pagination;
  if (!p) {
    return {
      page: fallback.page,
      size: fallback.size,
      totalItems: fallback.count,
      totalPages: 1,
    };
  }
  return { page: p.page + 1, size: p.size, totalItems: p.totalItems, totalPages: p.totalPages };
};

const fetchBalance = async (userId: string): Promise<MileageBalance> => {
  const res = await adminFetch(`${BASE}/users/${userId}/balance`);
  if (!res.ok) await throwFromResponse(res, '마일리지 잔액을 불러오지 못했습니다.');
  const env = (await res.json()) as BalanceEnvelope;
  return env.data ?? { userId, balance: 0 };
};

const fetchTransactions = async (
  q: MileageTransactionQuery,
): Promise<PagedResult<MileageTransaction>> => {
  const params = new URLSearchParams({
    page: String(Math.max(0, q.page - 1)),
    size: String(q.size),
  });
  if (q.userId) params.set('userId', q.userId);
  if (q.type) params.set('type', q.type);
  if (q.occurredFrom) params.set('occurredFrom', q.occurredFrom);
  if (q.occurredTo) params.set('occurredTo', q.occurredTo);
  const res = await adminFetch(`${BASE}/transactions?${params.toString()}`);
  if (!res.ok) await throwFromResponse(res, '마일리지 거래내역을 불러오지 못했습니다.');
  const env = (await res.json()) as PageEnvelope<MileageTransaction>;
  const items = env.data?.content ?? [];
  return { items, pagination: toPagination(env.meta, { page: q.page, size: q.size, count: items.length }) };
};

const fetchExpiring = async (q: MileageExpiringQuery): Promise<PagedResult<MileageEarnLot>> => {
  const params = new URLSearchParams({
    days: String(q.days),
    page: String(Math.max(0, q.page - 1)),
    size: String(q.size),
  });
  const res = await adminFetch(`${BASE}/expiring?${params.toString()}`);
  if (!res.ok) await throwFromResponse(res, '만료 임박 마일리지를 불러오지 못했습니다.');
  const env = (await res.json()) as PageEnvelope<MileageEarnLot>;
  const items = env.data?.content ?? [];
  return { items, pagination: toPagination(env.meta, { page: q.page, size: q.size, count: items.length }) };
};

export const useMileageBalanceQuery = (userId: string | null) =>
  useQuery<MileageBalance>({
    queryKey: mileageBalanceQueryKey(userId ?? ''),
    queryFn: () => fetchBalance(userId as string),
    enabled: Boolean(userId),
  });

export const useMileageTransactionsQuery = (q: MileageTransactionQuery, enabled = true) =>
  useQuery<PagedResult<MileageTransaction>>({
    queryKey: mileageTransactionsQueryKey(q),
    queryFn: () => fetchTransactions(q),
    enabled,
  });

export const useMileageExpiringQuery = (q: MileageExpiringQuery) =>
  useQuery<PagedResult<MileageEarnLot>>({
    queryKey: mileageExpiringQueryKey(q),
    queryFn: () => fetchExpiring(q),
  });

const postAdjust = async (
  path: string,
  body: MileageAdjustPayload | MileageBulkAdjustPayload,
): Promise<Response> => {
  const headers = { 'Idempotency-Key': idempotencyKeyFor('POST', path, body) };
  return adminFetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
};

export const useAdminMileageAdjustMutation = () => {
  const qc = useQueryClient();
  const invalidate = (userId?: string) => {
    void qc.invalidateQueries({ queryKey: ['admin', 'mileage'] });
    if (userId) void qc.invalidateQueries({ queryKey: mileageBalanceQueryKey(userId) });
  };

  const grant = useMutation({
    mutationFn: async (payload: MileageAdjustPayload): Promise<void> => {
      const res = await postAdjust(`${BASE}/grant`, payload);
      if (!res.ok) await throwFromResponse(res, '마일리지 지급에 실패했습니다.');
    },
    onSuccess: (_d, payload) => invalidate(payload.userId),
  });

  const deduct = useMutation({
    mutationFn: async (payload: MileageAdjustPayload): Promise<void> => {
      const res = await postAdjust(`${BASE}/deduct`, payload);
      if (!res.ok) await throwFromResponse(res, '마일리지 차감에 실패했습니다.');
    },
    onSuccess: (_d, payload) => invalidate(payload.userId),
  });

  const bulkGrant = useMutation({
    mutationFn: async (payload: MileageBulkAdjustPayload): Promise<BulkAdjustResult> => {
      const res = await postAdjust(`${BASE}/bulk-grant`, payload);
      if (!res.ok) await throwFromResponse(res, '마일리지 일괄 지급에 실패했습니다.');
      const env = (await res.json()) as BulkResultEnvelope;
      if (!env.data) throw new Error('일괄 지급 응답이 비어 있습니다.');
      return env.data;
    },
    onSuccess: () => invalidate(),
  });

  const bulkDeduct = useMutation({
    mutationFn: async (payload: MileageBulkAdjustPayload): Promise<BulkAdjustResult> => {
      const res = await postAdjust(`${BASE}/bulk-deduct`, payload);
      if (!res.ok) await throwFromResponse(res, '마일리지 일괄 차감에 실패했습니다.');
      const env = (await res.json()) as BulkResultEnvelope;
      if (!env.data) throw new Error('일괄 차감 응답이 비어 있습니다.');
      return env.data;
    },
    onSuccess: () => invalidate(),
  });

  return { grant, deduct, bulkGrant, bulkDeduct };
};
